import { FC } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  PieChartProps,
} from "recharts";
import { ChartContainer } from "@/components/ui/chart-container";
import { formatCurrency } from "@/utils/formatters";

interface BasePieChartProps extends PieChartProps {
  data: Array<{ name: string; value: number }>; // Data format with name and value keys
  colors: string[]; // Colors for the pie slices
  formatter?: (value: number | string) => string; // Formatter for values in tooltips
  legendTitle?: string; // Optional title for the legend
}

export const BasePieChart: FC<BasePieChartProps> = ({
  data,
  colors,
  formatter = formatCurrency,
  legendTitle = "Legend", // Default legend title
  ...restProps
}) => {
  return (
    <ChartContainer aria-label="Pie chart visualization">
      <ResponsiveContainer>
        <PieChart {...restProps} aria-label="Responsive pie chart">
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label={({ name, value }) => `${name}: ${formatter(value)}`} // Labels with formatted values
            aria-label="Pie chart slice"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry.name}`}
                fill={colors[index % colors.length]} // Cycle through colors
                aria-label={`Slice for ${entry.name}, value: ${formatter(entry.value)}`}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number | string) => formatter(value)}
            labelFormatter={(label) => `Category: ${label}`} // Add context to labels
          />
          <Legend
            payload={data.map((entry, index) => ({
              id: entry.name,
              type: "square",
              value: `${entry.name}`,
              color: colors[index % colors.length],
            }))}
          >
            <div aria-label="Legend title">{legendTitle}</div>
          </Legend>
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
